import { useEffect, useRef } from "react";
import { trackBlogEngagement, trackBlogView } from "../utils/blogAnalytics";

/**
 * Tracks a blog view on mount, then measures active reading time (paused while the tab is
 * hidden) and max scroll depth, flushing a single engagement beacon when the reader leaves.
 */
export default function useBlogEngagementTracking(slug) {
  const activeMsRef = useRef(0);
  const visibleSinceRef = useRef(null);
  const maxScrollDepthRef = useRef(0);
  const flushedRef = useRef(false);

  useEffect(() => {
    if (!slug) return undefined;

    trackBlogView(slug);

    flushedRef.current = false;
    activeMsRef.current = 0;
    maxScrollDepthRef.current = 0;
    visibleSinceRef.current = document.visibilityState === "visible" ? Date.now() : null;

    const accumulateActiveTime = () => {
      if (visibleSinceRef.current !== null) {
        activeMsRef.current += Date.now() - visibleSinceRef.current;
        visibleSinceRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        visibleSinceRef.current = Date.now();
      } else {
        accumulateActiveTime();
      }
    };

    const handleScroll = () => {
      const docEl = document.documentElement;
      const scrollableHeight = docEl.scrollHeight - docEl.clientHeight;
      if (scrollableHeight <= 0) {
        maxScrollDepthRef.current = 100;
        return;
      }
      const depth = ((window.scrollY + docEl.clientHeight) / docEl.scrollHeight) * 100;
      maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.min(100, depth));
    };

    const flush = () => {
      if (flushedRef.current) return;
      flushedRef.current = true;
      accumulateActiveTime();
      trackBlogEngagement(slug, {
        readingTimeMs: activeMsRef.current,
        scrollDepthPercent: maxScrollDepthRef.current,
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("pagehide", flush);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("pagehide", flush);
      flush();
    };
  }, [slug]);
}
